import { useQuery } from '@tanstack/react-query';
import { fabric } from 'fabric';
import { FabricJSCanvas, useFabricJSEditor } from 'fabricjs-react';
import _ from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useUpdateStatisticsMutation } from 'api/adminApi';
import { getDashboard, getMapDashboard, getPlantImage } from 'api/dashboardApi';
import {
  getPartList,
  getSectionListByFactory,
  getZoneListByFactory,
} from 'api/deviceApi';
import { DeviceSearchRequest } from 'api/DeviceRequest';
import {
  dashboardKeys,
  mapDashboardKeys,
  partKeys,
  plantImageKeys,
  sectionKeys,
  zoneKeys,
} from 'api/quires/queryKeys';
import ZoneInfoModal from 'components/device/position/ZoneInfoModal';
import DeviceSearchBox from 'components/searchbox/DeviceSearchBox';
import Factory from 'model/Factory';
import SectionBoard from 'model/SectionBoard';
import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { DEVICE_MODAL_TYPE, ROLE_TYPE, STATISTICS_TYPE } from 'types/enumType';
import {
  createRectByEquipmentAndRatioInStatusBoard,
  getScaleAndRatio,
} from 'util/commonUtil';

import { decryptString } from '../../util/encryptUtil';

export default function StatusBoard() {
  const ZONE_SQUARE_IMAGE = `${process.env.PUBLIC_URL}/assets/images/content/zone-square.png`;

  const userInfo = useRecoilValue(userInfoState);
  const role = decryptString(userInfo.role);
  const isAdmin = role === ROLE_TYPE.ADMIN;
  const [searchRequest, setSearchRequest] = useState<DeviceSearchRequest>({
    factoryId: '',
  });
  const [plantInfo, setPlantInfo] = useState<Factory | null>(null);
  const [planImage, setPlanImage] = useState<string>('');
  const [imgCanvas, setImgCanvas] = useState<fabric.Image>();

  const { editor, onReady } = useFabricJSEditor();

  const [activeSection, setActiveSection] = useState<SectionBoard | null>(null);
  const [posX, setPosX] = useState<number>(0);
  const [posY, setPosY] = useState<number>(0);

  const [isZoneInfoModalOpen, setIsZoneInfoModalOpen] = useState(false);

  const { data: plantImageInfo } = useQuery(
    plantImageKeys.all,
    () => getPlantImage(),
    { enabled: !isAdmin },
  );
  const {
    data: partList,
    refetch: refetchPart,
    remove: removePart,
  } = useQuery(
    partKeys.list(searchRequest.factoryId),
    () => getPartList(searchRequest),
    { enabled: !isAdmin },
  );
  const {
    data: zoneList,
    refetch: refetchZone,
    remove: removeZone,
  } = useQuery(
    zoneKeys.listByFactory(searchRequest.factoryId!),
    () => getZoneListByFactory(searchRequest),
    { enabled: !isAdmin },
  );
  const {
    data: sectionList,
    refetch: refetchSection,
    remove: removeSection,
  } = useQuery(
    sectionKeys.listByFactory(searchRequest.factoryId!),
    () => getSectionListByFactory(searchRequest),
    { enabled: false },
  );
  const {
    data: activeZoneList,
    refetch: refetchActiveZone,
    remove: removeActiveZone,
  } = useQuery(mapDashboardKeys.all, () => getMapDashboard(), {
    enabled: !isAdmin,
  });

  const { data } = useQuery(dashboardKeys.detail, () => getDashboard());

  useEffect(() => {
    if (activeZoneList) {
      removeSection();
      refetchSection();
    }
  }, [activeZoneList]);

  useEffect(() => {
    if (!sectionList) {
      return;
    }

    const tmpImage = isAdmin
      ? (plantInfo?.image ?? '')
      : (plantImageInfo?.plantImage ?? '');
    setPlanImage(tmpImage);
  }, [sectionList]);

  const resizeCanvas = (ww: number, wh: number) => {
    if (!imgCanvas || !editor?.canvas || !editor.canvas.backgroundImage) return;

    const { scale: newScale } = getScaleAndRatio(imgCanvas, {
      width: ww,
      height: wh,
    });
    const iw = imgCanvas.width!;
    const ih = imgCanvas.height!;

    const afterWidth = newScale * iw;
    const afterHeight = newScale * ih;

    // 배경 이미지 스케일
    imgCanvas.scale(newScale);
    editor?.canvas.setBackgroundImage(
      imgCanvas,
      editor?.canvas.renderAll.bind(editor?.canvas),
      { scaleX: newScale, scaleY: newScale },
    );

    editor?.canvas.setDimensions({
      width: afterWidth,
      height: afterHeight,
    });

    // 기존 오브젝트 제거
    const background = editor.canvas.backgroundImage!;
    editor.canvas.clear();
    editor.canvas.setBackgroundImage(
      background,
      editor?.canvas.renderAll.bind(editor?.canvas),
    );

    if (partList && partList?.list?.length > 0) {
      editor.canvas.add(
        ...partList.list.map((partItem) =>
          createRectByEquipmentAndRatioInStatusBoard(
            partItem,
            DEVICE_MODAL_TYPE.PART,
            newScale,
            false,
          ),
        ),
      );
    }
    if (zoneList && zoneList?.list?.length > 0) {
      editor.canvas.add(
        ...zoneList.list.map((zoneItem) =>
          createRectByEquipmentAndRatioInStatusBoard(
            zoneItem,
            DEVICE_MODAL_TYPE.AREA,
            newScale,
            false,
          ),
        ),
      );
    }

    if (sectionList && sectionList?.list?.length > 0) {
      const activeSectionIdList =
        activeZoneList?.list?.map((item) => item.sectionId) ?? [];
      sectionList.list.forEach((zoneItem) => {
        const isActive = activeSectionIdList?.includes(zoneItem.id);
        const rect = createRectByEquipmentAndRatioInStatusBoard(
          zoneItem,
          DEVICE_MODAL_TYPE.SECTION,
          newScale,
          false,
        );
        if (!isActive) {
          editor.canvas.add(rect);
        } else {
          fabric.Image.fromURL(ZONE_SQUARE_IMAGE, (oImg) => {
            const { x, y } = rect.getCenterPoint();
            oImg.scale(newScale);
            oImg.setOptions({
              left: x,
              top: y,
              width: 50,
              height: 50,
              originX: 'center',
              originY: 'center',
            });
            const group = new fabric.Group([rect, oImg], {
              selectable: false,
              name: `${DEVICE_MODAL_TYPE.SECTION}/${zoneItem.id}/active`,
            });
            editor.canvas.add(group);
          });
        }
      });
    }
  };

  const handleCanvasReady = (canvas: fabric.Canvas) => {
    fabric.Image.fromURL(planImage, (img) => {
      setImgCanvas(img);

      const { scale } = getScaleAndRatio(img, {
        width: window.innerWidth,
        height: window.innerHeight,
      });

      img.scale(scale);
      canvas.setDimensions({
        width: scale * img.width!,
        height: scale * img.height!,
      });
      canvas.setBackgroundImage(img, canvas.renderAll.bind(canvas));

      if (partList && partList?.list?.length > 0) {
        canvas.add(
          ...partList.list.map((partItem) =>
            createRectByEquipmentAndRatioInStatusBoard(
              partItem,
              DEVICE_MODAL_TYPE.PART,
              scale,
              false,
            ),
          ),
        );
      }
      if (zoneList && zoneList?.list?.length > 0) {
        canvas.add(
          ...zoneList.list.map((zoneItem) =>
            createRectByEquipmentAndRatioInStatusBoard(
              zoneItem,
              DEVICE_MODAL_TYPE.AREA,
              scale,
              false,
            ),
          ),
        );
      }

      if (sectionList && sectionList?.list?.length > 0) {
        const activeSectionIdList =
          activeZoneList?.list?.map((item) => item.sectionId) ?? [];
        sectionList.list.forEach((zoneItem) => {
          const isActive = activeSectionIdList?.includes(zoneItem.id);
          const rect = createRectByEquipmentAndRatioInStatusBoard(
            zoneItem,
            DEVICE_MODAL_TYPE.SECTION,
            scale,
            false,
          );

          if (!isActive) {
            canvas.add(rect);
          } else {
            fabric.Image.fromURL(ZONE_SQUARE_IMAGE, (oImg) => {
              const { x, y } = rect.getCenterPoint();
              oImg.scale(scale);
              oImg.setOptions({
                left: x,
                top: y,
                width: 50,
                height: 50,
                originX: 'center',
                originY: 'center',
              });
              const group = new fabric.Group([rect, oImg], {
                selectable: false,
                name: `${DEVICE_MODAL_TYPE.SECTION}/${zoneItem.id}/active`,
              });
              canvas.add(group);
            });
          }
        });
      }
      // canvas.renderAll();
      onReady(canvas);
    });

    canvas.selection = false;
  };

  const debounceResize = _.debounce(resizeCanvas, 200);
  useEffect(() => {
    const handleWindowResize = () => {
      if (!editor?.canvas) {
        return;
      }

      const { innerWidth: width, innerHeight: height } = window;

      debounceResize(width, height);
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [editor]);

  const handleCloseZoneInfoModal = () => {
    setIsZoneInfoModalOpen(false);
  };

  const handleSearchSubmit = (
    factoryInfo: Factory | null,
    event?: FormEvent,
  ) => {
    if (event) {
      event.preventDefault();
    }

    setSearchRequest((prev) => ({
      ...prev,
      factoryId: (factoryInfo?.id ?? '').toString(),
    }));

    if (editor?.canvas) {
      editor.canvas.clear();
    }

    setPlanImage('');
    setPlantInfo(factoryInfo);

    setTimeout(() => {
      removePart();
      removeZone();
      removeActiveZone();

      refetchPart();
      refetchZone();
      refetchActiveZone();
    }, 100);
  };

  const handleClickActiveZone = (e: fabric.IEvent<MouseEvent>) => {
    const selectObj = e.target;
    if (!selectObj) {
      return false;
    }

    const [type, typeId, isActive] = selectObj.name!.split('/');

    if (type === DEVICE_MODAL_TYPE.SECTION && isActive === 'active') {
      const { clientX, clientY } = e.e;
      setPosX(clientX);
      setPosY(clientY);

      setActiveSection(
        activeZoneList!.list.find((item) => item.sectionId === +typeId)!,
      );
      // modal open
      const handleOpenZoneInfoModal = () => {
        setIsZoneInfoModalOpen(true);
      };
      handleOpenZoneInfoModal();

      return true;
    }

    return false;
  };

  useEffect(() => {
    if (!editor?.canvas) {
      return;
    }

    const enablePanning = () => {
      let isDragging = false;
      let lastX: number = 0;
      let lastY: number = 0;

      editor?.canvas.on('mouse:down', (event) => {
        const pointer = editor?.canvas.getPointer(event.e);
        lastX = pointer.x;
        lastY = pointer.y;
        isDragging = true;
      });

      editor?.canvas.on('mouse:move', (event) => {
        if (!isDragging) return;

        const pointer = editor.canvas.getPointer(event.e);
        lastX = pointer.x;
        lastY = pointer.y;

        const delta = new fabric.Point(event.e.movementX, event.e.movementY);
        editor.canvas.relativePan(delta);
      });

      editor?.canvas.on('mouse:up', (event) => {
        isDragging = false;

        const pointer = editor?.canvas.getPointer(event.e);
        const diffX = Math.abs(pointer.x - lastX);
        const diffY = Math.abs(pointer.y - lastY);

        if (diffX === 0 && diffY === 0) {
          handleClickActiveZone(event);
        }
      });

      editor?.canvas.on('mouse:wheel', (opt) => {
        const delta = opt.e.deltaY;
        let zoom = editor.canvas.getZoom();
        zoom *= 0.999 ** delta;
        if (zoom > 20) zoom = 20;
        if (zoom <= 1) zoom = 1;
        editor.canvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
        opt.e.preventDefault();
        opt.e.stopPropagation();

        const vpt = editor.canvas.viewportTransform;
        const canvasW = editor.canvas.getWidth();
        const canvasH = editor.canvas.getHeight();
        if (vpt && zoom === 1) {
          if (zoom < 400 / canvasW) {
            vpt[4] = 200 - (canvasW * zoom) / 2;
            vpt[5] = 200 - (canvasW * zoom) / 2;
          } else {
            if (vpt[4] >= 0) {
              vpt[4] = 0;
            } else if (vpt[4] < editor.canvas.getWidth() - canvasW * zoom) {
              vpt[4] = editor.canvas.getWidth() - canvasW * zoom;
            }
            if (vpt[5] >= 0) {
              vpt[5] = 0;
            } else if (vpt[5] < editor.canvas.getHeight() - canvasH * zoom) {
              vpt[5] = editor.canvas.getHeight() - canvasH * zoom;
            }
          }
        }
      });
    };

    enablePanning();
  }, [editor?.canvas]);

  const { pathname } = useLocation();
  const { mutate } = useUpdateStatisticsMutation();

  useEffect(() => {
    mutate(STATISTICS_TYPE.DASHBOARD, {});
  }, [pathname]);

  return (
    <div className={'board-wrap'}>
      <div className={'dash-wrap'}>
        {role === ROLE_TYPE.ADMIN && (
          <DeviceSearchBox
            searchRequest={searchRequest}
            isLoading={false}
            onSubmit={handleSearchSubmit}
          />
        )}
        {data?.info && (
          <>
            <section className={'main-table'}>
              <table className={'tbl_hl01'}>
                <colgroup>
                  <col width={'32%'} />
                  <col width={'40%'} />
                  <col width={'28%'} />
                </colgroup>
                <tbody>
                  <tr>
                    <th className={'under'} rowSpan={3}>
                      <Link to={'/loto-status/conclusion'}>
                        {'작업허가서 '}
                        <br />
                        <b>{`(${
                          data.info.orderLockAttachCnt +
                          data.info.orderLockConfirmCnt +
                          data.info.orderUnlockApproveCnt
                        })`}</b>
                      </Link>
                    </th>

                    <td>{'부착 중'}</td>
                    <td>{data.info.orderLockAttachCnt}</td>
                  </tr>
                  <tr>
                    <td className={'nowrap'}>{'부착 확정'}</td>
                    <td>{data.info.orderLockConfirmCnt}</td>
                  </tr>
                  <tr>
                    <td className={'nowrap'}>{'Loto Box Open'}</td>
                    <td>{data.info.orderUnlockApproveCnt}</td>
                  </tr>
                  <tr>
                    <th className={'under'} rowSpan={3}>
                      <Link to={'/loto-status/action'}>
                        {'Lock '}
                        <br />
                        <b>{`(${data.info.totalSettingCnt})`}</b>
                      </Link>
                    </th>
                    <td>{'미부착'}</td>
                    <td>{data.info.totalNothingCnt}</td>
                  </tr>
                  <tr>
                    <td>{'부착'}</td>
                    <td>{data.info.totalLockedCnt}</td>
                  </tr>
                  <tr>
                    <td>{'제거'}</td>
                    <td>{data.info.totalUnlockCnt}</td>
                  </tr>
                </tbody>
              </table>
            </section>
            <section className={'main-progressbar'}>
              <table className={'tbl_hl01 tbl_ht01'}>
                <colgroup>
                  <col width={'20%'} />
                  <col width={'20%'} />
                  <col width={'20%'} />
                  <col width={'20%'} />
                  <col width={'20%'} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>{'구분'}</td>
                    <th>{'생산'}</th>
                    <th>{'전기'}</th>
                    <th>{'기계'}</th>
                    <th>{'현장'}</th>
                  </tr>
                  <tr>
                    <th>{'미부착'}</th>
                    <td>{data.info.prodNothingCnt}</td>
                    <td>{data.info.elecNothingCnt}</td>
                    <td>{data.info.mechNothingCnt}</td>
                    <td>{data.info.etcNothingCnt}</td>
                  </tr>
                  <tr>
                    <th>{'부착'}</th>
                    <td>{data.info.prodLockedCnt}</td>
                    <td>{data.info.elecLockedCnt}</td>
                    <td>{data.info.mechLockedCnt}</td>
                    <td>{data.info.etcLockedCnt}</td>
                  </tr>
                  <tr>
                    <th>{'제거'}</th>
                    <td>{data.info.prodUnlockCnt}</td>
                    <td>{data.info.elecUnlockCnt}</td>
                    <td>{data.info.mechUnlockCnt}</td>
                    <td>{data.info.etcUnlockCnt}</td>
                  </tr>
                </tbody>
              </table>
            </section>
          </>
        )}
      </div>
      <div className={'canvas-container'}>
        {planImage && (
          <FabricJSCanvas
            className={'sample-canvas'}
            onReady={handleCanvasReady}
          />
        )}
      </div>
      <ZoneInfoModal
        info={activeSection}
        isOpen={isZoneInfoModalOpen}
        onClose={handleCloseZoneInfoModal}
        posX={posX}
        posY={posY}
      />
    </div>
  );
}
